import { Navigate, Outlet } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Building2,
  Settings,
  Ticket,
  UserCog,
  Users,
  Workflow,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { AppShell, type SidebarNavItem } from '@/components/AppShell';
import { ADMIN_TABS } from './AdminTabs';
import { TimerWidget } from './TimerWidget';

/**
 * AdminLayout — sidebar shell for every /admin/* route. Nav items come from
 * the canonical ADMIN_TABS list (ADM-01); this file only adds icons.
 *
 * Wrapped in <RequireAuth> at the router level so every admin route requires
 * an active API-backed session.
 */

const ADMIN_ICONS: Record<string, SidebarNavItem['icon']> = {
  tickets: Ticket,
  companies: Building2,
  users: Users,
  knowledgebase: BookOpen,
  workflows: Workflow,
  settings: Settings,
  reports: BarChart3,
  impersonation: UserCog,
};

const NAV_ITEMS: readonly SidebarNavItem[] = ADMIN_TABS.map((tab) => ({
  key: tab.key,
  label: tab.label,
  to: tab.to,
  icon: ADMIN_ICONS[tab.key] ?? Building2,
}));

export default function AdminLayout() {
  const { user, role } = useAuth();

  // Role gate — CLIENT users belong in the portal shell, not the admin shell.
  // Symmetric mirror of PortalLayout's non-CLIENT redirect. The API remains
  // the authoritative enforcement boundary (every /api/admin/* route is gated
  // by authGuard({ roles: ['ADMIN'] })); this UI redirect is defense-in-depth
  // and UX only. Closes v1.0-MILESTONE-AUDIT UX-1.
  if (user && role === 'CLIENT') {
    return <Navigate to="/portal" replace />;
  }

  return (
    <AppShell
      title="Ventin Support — Admin"
      items={NAV_ITEMS}
      profileTo="/admin/profile"
      topRight={<TimerWidget />}
    >
      <Outlet />
    </AppShell>
  );
}
